/**
 * usePersonaColor — wraps getPersonaColor with the CouncilContext color_map.
 *
 * If used outside CouncilProvider, falls back to the two-tier resolution
 * (persona.accent_color → name-hash). No throw — color resolution is a
 * cosmetic concern and should never crash chrome.
 */
import { useContext } from 'react';

import { CouncilContext } from '../contexts/CouncilContext';
import { getPersonaColor, type PersonaColor } from '../utils/personaColor';

// SP2a — the synthetic "Blueprint Foreman" that hosts every blueprint's intent
// spine. Not a real persona/roster row, so it never appears in personasById and
// would otherwise resolve to a bare "Peer 00000000" slug. Keep in sync with the
// backend BLUEPRINT_FOREMAN_ID.
export const BLUEPRINT_FOREMAN_ID = '00000000-0000-0000-0000-00000b1f0001';
const BLUEPRINT_FOREMAN_NAME = 'Blueprint Foreman';

export function usePersonaColor(
  persona: { id: string; accent_color?: number | null; name: string },
): PersonaColor {
  const ctx = useContext(CouncilContext);
  // Give the Foreman a stable, distinct tint (name-hash off its fixed name).
  if (persona.id === BLUEPRINT_FOREMAN_ID) {
    return getPersonaColor({ id: persona.id, accent_color: null, name: BLUEPRINT_FOREMAN_NAME });
  }
  return getPersonaColor(persona, ctx?.state?.metadata?.persona_color_map);
}

/**
 * usePersonaName — null-safe persona display name resolution.
 *
 * Sibling of usePersonaColor (same module to keep persona resolution
 * patterns co-located; renderers import both from one path).
 *
 * Resolution:
 *   1. personas[id]?.name
 *   2. `Peer ${id.slice(0,8)}` short-id slug
 *   3. `Peer ?` when id is undefined
 *
 * Accepts null context, undefined persona — never throws.
 */
export function usePersonaName(personaId: string | undefined): string {
  const ctx = useContext(CouncilContext);
  if (personaId === BLUEPRINT_FOREMAN_ID) return BLUEPRINT_FOREMAN_NAME;  // SP2a
  const persona = ctx?.state?.personasById?.[personaId ?? ''];
  if (persona?.name) return persona.name;
  if (!personaId) return 'Peer ?';
  return `Peer ${personaId.slice(0, 8)}`;
}
